<template>
  <div class="creation-wrapper">
    <basis-nav
      nav-title="创作内容"
      :back="back"
    />
    <div class="content-wrapper">
      <van-field
        v-model="creation.body"
        autosize
        show-word-limit
        required
        rows="4"
        type="textarea"
        maxlength="110"
        placeholder="正文内容（最多110字）"
        :error="bodyError"
      />
      <van-field
        v-model="creation.inscriber"
        rows="1"
        autosize
        type="textarea"
        maxlength="20"
        placeholder="落款内容（最多20字）"
      />
    </div>
    <div class="select-wrapper">
      <p class="title">一列多少个字</p>
      <selector
        v-model="creation.charactersPerLine"
        :columns="() => {
          const arr = []
          for(let i = 1; i < 100; i++){
            arr.push(i)
          }
          return arr
        }"
      />
    </div>
    <div class="select-wrapper">
      <p class="title">使用哪种纸张背景</p>
      <selector
        v-model="backgroundImage"
        :columns="() => ['白纸', '宣纸', '复古纸']"
      />
    </div>
    <div class="button-wrapper">
      <van-button
        class="button preview-button"
        loading-text="效果生成中..."
        :loading="previewLoading"
        @click="payTypeHandler(undefined)"
      >
        预览效果
      </van-button>

      <van-button
        style="margin-top: 10px;"
        class="button preview-button"
        @click="$router.push({
          name: 'creation-test',
          params: { ...$route.params }
        })"
      >智能创作 </van-button>
      <van-button
        v-if="!font.locked"
        class="button generate-button"
        loading-text="书帖生成中..."
        :loading="generateLoading"
        @click="payTypeHandler('vip')"
      >
        生成书帖
      </van-button>
    </div>
    <div class="popup-wrapper">
      <template v-if="font.locked">
        <!-- 生成书帖 -->
        <van-popup
          v-model="showCreated"
          round
          position="bottom"
          :overlay="false"
          :lock-scroll="false"
        >
          <div class="pay-type-wrapper">
            <p class="title">
              生成书帖
              <span
                v-if="vipsNeeded.length"
                class="create-vip-text"
                @click.stop="showVipList = true"
              >
                <!-- 开通 {{ font.vipNeeded.name }} 无限次使用 -->
                开通 VIP 无限次使用
                <van-icon name="arrow" />
              </span>
            </p>
            <p class="worth-wrapper">
              <template v-if="font.creditsNeeded">
                <span class="worth">{{ font.creditsNeeded }}积分</span>
              </template>
              <template v-if="font.cashNeeded">
                <span class="worth">¥<span v-money="font.cashNeeded" /></span>
              </template>
            </p>
            <div class="pay-button-wrapper">
              <van-button
                v-if="font.creditsNeeded"
                :class="['credits-button', {'only-one': !font.cashNeeded}]"
                @click="payTypeHandler('credits')"
              >积分支付</van-button>
              <van-button
                v-if="font.cashNeeded"
                :class="['cash-button', {'only-one': !font.creditsNeeded}]"
                @click="payTypeHandler('cash')"
              >微信支付</van-button>
              <template v-if="!font.creditsNeeded && !font.cashNeeded">
                <van-button
                  :class="['cash-button', {'only-one': !font.creditsNeeded && !font.cashNeeded}]"
                  @click="showVipList = true"
                >开通 VIP </van-button>
              </template>
            </div>
          </div>
        </van-popup>
        <!-- 积分支付 -->
        <basis-credits-popup
          v-model="showCredits"
          :credits-needed="font.creditsNeeded"
          @clickOverlay="clickOverlay({
            showCreated: true
          })"
          @pay="payByCreditsHandler"
        />
      </template>
      <!-- 提醒用户缺失的文字 -->
      <van-popup
        v-model="showUnavailableText"
        round
        overlay
        position="bottom"
        @click-overlay="clickOverlay({
          showCreated: true
        })"
      >
        <div class="unavailable-text-wrapper">
          <p class="tag">
            <span class="active">{{ unavailableText }}</span> 等字暂无字体生成, 内容将出现空白
          </p>
          <van-button class="continue-button" @click="payHandler">没关系，继续生成</van-button>
        </div>
      </van-popup>
      <!-- VIP 列表 -->
      <vip-list-popup
        v-model="showVipList"
        :vips-needed="vipsNeeded"
      />
    </div>
    <div class="overlay-wrapper">
      <!-- 普通浏览器中弹起的支付二维码 -->
      <wx-pay-overlay
        :overlay-status.sync="showWxPayOverlay"
        :qrcode="wxPayQrcode"
        @successed="wxPaySuccess"
      />
      <!-- 生成的图片 -->
      <van-overlay :show="showImg" @click="closeHandler">
        <div :class="['image-wrapper', createType]" @click.stop>
          <template v-if="createType === 'generate'">
            <van-image
              width="100%"
              height="100%"
              fit="contain"
              :src="imageUrl"
            />
            <span class="close" @click="closeHandler">
              <van-icon name="cross" />
            </span>
            <span class="tag">长按可保存到相册</span>
          </template>
          <template v-else>
            <div class="bg-img" :style="{backgroundImage: 'url('+ imageUrl +')'}" />
          </template>
        </div>
      </van-overlay>
    </div>
  </div>
</template>

<script>
import { BasisNav, BasisCreditsPopup } from '@/components/basis'
import VipListPopup from '@/components/VipListPopup'
import WxPayOverlay from '@/components/wx'
import Selector from './components/Selector'
import { isWechatBrowser, onBridgeReady } from '@/utils/wechat'
import { wxPayENV } from '@/config/wx.config'
import { mapGetters, mapActions } from 'vuex'
import { Creation, Font } from '@/api'
import { mixinWxShare } from '@/mixins'

export default {
  name: 'Creation',
  components: { BasisNav, BasisCreditsPopup, VipListPopup, WxPayOverlay, Selector },
  mixins: [mixinWxShare],
  data: () => ({
    font: {},
    showVipList: false, // 显示想开通的 VIP 列表
    bodyError: false, // 正文必传, 错误提示状态
    previewLoading: false, // 加载预览效果文案
    generateLoading: false, // 加载生成书帖文案
    showCreated: true, // 是否展示生成书帖按钮 popup
    showCredits: false, // 是否展示积分扣除模块 popup
    showUnavailableText: false, // 是否展示不支持文字 popup
    showImg: false, // 是否展示生成的图片 overlay
    showWxPayOverlay: false, // 是否弹起 普通浏览器中 微信支付二维码
    createType: undefined, // preview：预览动作返回的, generate：生成动作涉及到支付
    payType: undefined, // undefined: 预览, credits: 积分, vip: VIP, cash: 现金
    unavailableText: '', // 不支持的文字
    wxPayQrcode: '', // 普通浏览器中的微信支付二维码
    createId: null, // 创建的集字 ID
    backgroundImage: '白纸',
    imageUrl: '',
    creation: {
      body: '',
      inscriber: '',
      fontId: null,
      charactersPerLine: 1
    }
  }),
  computed: {
    ...mapGetters(['credits']),
    vipsNeeded () {
      return this.font.vipsNeeded
    }
  },
  async mounted () {
    const { id } = this.$route.params
    this.creation.fontId = id
    try {
      const { font } = await Font.detail({ id })
      this.font = font
    } catch (error) {
      console.log(error)
    }
  },
  methods: {
    ...mapActions(['checkCredits', 'updateVip']),
    back () {
      this.$router.push({
        name: 'teacher-creations'
      })
    },
    selectConfirm ({ value }) {
      this.creation.charactersPerLine = value
    },
    selectCancel () {
      // this.creation.model = ''
    },
    closeHandler () {
      this.showImg = false
      this.showCreated = true
    },
    clickOverlay (target) {
      for (const key in target) {
        if (Object.prototype.hasOwnProperty.call(target, key)) {
          this[key] = target[key]
        }
      }
    },
    async checkFonts (text) {
      try {
        const { unavailableText } = await Font.check({ id: this.font.id, text })
        if (unavailableText) {
          return Promise.reject(unavailableText)
        } else {
          return Promise.resolve('')
        }
      } catch (error) {
        console.log(error)
      }
    },
    async wxPaySuccess () {
      try {
        const { imageUrl } = await Creation.generate({
          id: this.createId,
          backgroundImage: this.backgroundImage,
          payType: this.payType
        })
        this.imageUrl = imageUrl
        this.showImg = true
      } catch (error) {
        console.log(error)
      }
    },
    // 支付 ---------
    async payTypeHandler (payType) {
      const { body } = this.creation
      if (!body) {
        this.bodyError = true
        return
      }
      this.bodyError = false
      this.payType = payType
      this.showCreated = false
      try {
        await this.checkFonts(body)
        this.payHandler()
      } catch (text) {
        this.unavailableText = text.split('').join(', ')
        this.showUnavailableText = true
      }
    },
    async payHandler () {
      try {
        const closeKeys = ['showCreated', 'showCredits', 'showUnavailableText']
        closeKeys.forEach(key => {
          this[key] = false
        })
        const { creation } = await Creation.create({ creation: this.creation })
        this.createId = creation.id
        switch (this.payType) {
          case undefined:
            // 预览
            this.createPreview()
            break
          case 'credits':
            // 积分支付
            this.showCredits = true
            break
          case 'cash': case 'vip':
            // 现金 / vip
            this.createCourse()
            break
        }
      } catch (error) {
        console.log(error)
      }
    },
    // 预览作品
    async createPreview () {
      try {
        this.createType = 'preview'
        this.previewLoading = true
        const { imageUrl } = await Creation.preview({ id: this.createId, backgroundImage: this.backgroundImage })
        this.imageUrl = imageUrl
        this.previewLoading = false
        this.showImg = true
      } catch (error) {
        this.showCreated = true
        this.previewLoading = false
        console.log(error)
      }
    },
    // 积分支付
    async payByCreditsHandler (callback) {
      try {
        await this.createCourse()
        callback()
        // 更新 积分
        this.checkCredits()
      } catch (error) {
        callback()
        console.log(error)
      }
    },
    // 金额 cash / 积分 credits / vip
    async createCourse () {
      try {
        this.generateLoading = true
        this.createType = 'generate'
        const { imageUrl, wxPayPayload, wxPayQrcode, wxPayUrl } = await Creation.generate({
          id: this.createId,
          backgroundImage: this.backgroundImage,
          payType: this.payType
        })
        switch (this.payType) {
          case 'cash':
            if (isWechatBrowser()) {
              // 微信浏览器
              onBridgeReady(wxPayPayload).then(() => {
                this.$notify({ type: 'success', message: '支付成功' })
                this.wxPaySuccess()
              }).catch(() => {
                this.showCreated = true
                this.$notify({ type: 'danger', message: '支付失败' })
              })
            } else {
              // 普通浏览器
              this.showCredits = false
              this.showWxPayOverlay = true
              if (wxPayENV === 'NATIVE') {
                this.wxPayQrcode = wxPayQrcode
              } else {
                window.location = wxPayUrl
              }
            }
            break
          case 'credits': case 'vip':
            this.imageUrl = imageUrl
            this.showCredits = false
            this.generateLoading = false
            this.showImg = true
            break
        }
      } catch (error) {
        this.showCreated = true
        this.generateLoading = false
        console.log(error)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.creation-wrapper {
  @include mh-bgc(100vh, $color-bg);
  padding-bottom: 124px;
  .content-wrapper {
    padding: 16px;
    background-color: #fff;
    position: relative;
    &::after {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 2px;
      background: -webkit-repeating-linear-gradient(135deg, #f5d9a9 0, #f5d9a9 20%, transparent 0, transparent 25%, #e8b271 0, #e8b271 45%, transparent 0, transparent 50%);
      background: repeating-linear-gradient(135deg, #f5d9a9 0, #f5d9a9 20%, transparent 0, transparent 25%, #e8b271 0, #e8b271 45%, transparent 0, transparent 50%);
      background-size: 80px;
      content: '';
    }
    .van-field {
      border-radius:4px;
      background-color: $color-bg;
      & + .van-field {
        margin-top: 16px;
      }
    }
  }
  .select-wrapper {
    margin-top: 16px;
    border-radius:8px;
    padding: 12px 16px;
    background-color: #fff;
    .title {
      @include sc($font-size-xlg, $color-text-heavy);
      @include lw(25px, 500);
      padding-bottom: 12px;
    }
  }
  .button-wrapper {
    padding: 24px 16px;
    .button {
      font-size: $font-size-lg;
      &.preview-button {
        @include plain-button(100%, 39px);
      }
      &.generate-button {
        margin-top: 16px;
        @include lg-radius(#ECCEA2, #E7B170, 4px);
        @include normal-button(100%, 39px);
      }
    }
  }
  .popup-wrapper {
    .pay-type-wrapper {
      padding: 12px 16px 16px;
      .title {
        @include sc($font-size-xlg, $color-text-heavy);
        @include lw(18px, 500);
        padding-bottom: 8px;
        .create-vip-text {
          @include sc($font-size-base, #FFA119);
          float: right;
          .van-icon {
            vertical-align: middle;
          }
        }
      }
      .worth-wrapper {
        @include sc($font-size-md, #F24724);
        @include lw(14px);
        padding-bottom: 18px;
        .worth {
          + .worth {
            &::before {
              content: " & ";
            }
          }
        }
      }
      .pay-button-wrapper {
        @include clear;
        .only-one {
          @include wh(100% !important, 39px);
        }
        .credits-button {
          @include plain-button(119px, 39px);
          float: left;
          font-size: $font-size-lg;
        }
        .cash-button {
          @include lg-radius(#ECCEA2, #E7B170, 4px);
          @include normal-button(208px, 39px);
          float: right;
          font-size: $font-size-lg;
        }
      }
    }
    .pay-by-credits-wrapper {
      padding: 16px;
      .title {
        position: relative;
        padding-bottom: 16px;
        .label-title {
          @include sc($font-size-base, $color-text-light);
          @include lw(17px, 500);
        }
        .credits-count {
          @include sc($font-size-md, #F24724);
          @include lw(14px);
          padding-top: 8px;
        }
        .create-vip-text {
          @include sc($font-size-base, #FFA119);
          position: absolute;
          top: 12px;
          right: 0;
          .van-icon {
            vertical-align: middle;
          }
        }
      }
      .pay-button-wrapper {
        .credits-button {
          font-size: $font-size-lg;
          @include lg-radius(#ECCEA2, #E7B170, 4px);
          @include normal-button(100%, 39px);
        }
      }
    }
    .unavailable-text-wrapper {
      text-align: center;
      padding: 16px;
      .tag {
        @include sc($font-size-lg, $color-text-heavy);
        padding: 32px 0;
        .active {
          color: $color-error;
        }
      }
      .continue-button {
        @include lg-radius(#ECCEA2, #E7B170, 4px);
        @include normal-button(100%, 39px);
      }
    }
  }
  .overlay-wrapper {
    .image-wrapper {
      background-color: #fff;
      position: relative;
      &.preview {
        @include wh(282px, 474px);
        @include ct;
        border-radius:8px;
        overflow: hidden;
        .bg-img {
          @include wh(100%, 100%);
          background-size: contain;
          background-position: center;
          background-repeat: no-repeat;
        }
      }
      &.generate {
        @include wh(100%, 100%);
        .close {
          @include wh(20px, 20px);
          @include tc(22px);
          border-radius: 50%;
          color: $color-text-medium;
          position: absolute;
          right: 16px;
          top: 16px;
        }
        .tag {
          @include plain-button(136px, 28px);
          @include tc(28px);
          position: absolute;
          right: 16px;
          bottom: 12px;
          border-radius: 20px;
        }
      }
    }
  }
}
</style>
